import { HelpCircle, X } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface HelpTooltipProps {
  content: string;
  title?: string;
  className?: string;
}

export function HelpTooltip({ content, title, className = '' }: HelpTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative inline-block ${className}`} ref={tooltipRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#D1D5DB] hover:text-[#EBFFEE] transition-colors focus:outline-none focus:ring-2 focus:ring-[#9B1421] focus:ring-offset-2 focus:ring-offset-[#111010]"
        aria-label="Help"
      >
        <HelpCircle size={16} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-64 p-4 mt-2 bg-[#1A1818] border border-[#504A4A] rounded-lg shadow-xl left-0 top-full">
          {title && (
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-semibold text-[#EBFFEE]">{title}</h4>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-[#D1D5DB] hover:text-[#EBFFEE] transition-colors"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <p className="text-xs text-[#D1D5DB] leading-relaxed">{content}</p>
        </div>
      )}
    </div>
  );
}
